import React, { useEffect, useState } from "react";
import { ShieldCheck, Plus, Pencil, Trash2, Search } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import { Badge2 } from "@/components/ComplianceBadge";
import { PageHeader } from "@/components/CrudModule";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const ROLES = ["viewer", "editor", "manager", "admin"];
const DEPTS = ["Usinagem", "Testes", "Pintura", "Instrumentacao", "Montagem", "Qualidade", "Caldeiraria", "Administrativo", "Geral"];
const ROLE_DESC = {
  viewer: "Somente visualização",
  editor: "Cria/edita registros e envia documentos",
  manager: "Editor + exclusão e configurações",
  admin: "Acesso total + gestão de usuários",
};
const ROLE_COLORS = { viewer: "slate", editor: "blue", manager: "yellow", admin: "green" };

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/users"); setUsers(data); } catch { toast.error("Falha ao carregar usuários"); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ name: "", email: "", password: "", role: "viewer", department: "Geral" }); setEditing(null); setOpen(true); };
  const openEdit = (u) => { setForm({ name: u.name, email: u.email, role: u.role, department: u.department, active: u.active, password: "" }); setEditing(u); setOpen(true); };
  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.name || (!editing && (!form.email || !form.password))) { toast.error("Preencha nome, e-mail e senha"); return; }
    setSaving(true);
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role, department: form.department, active: form.active };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("Usuário atualizado");
      } else {
        await api.post("/users", form);
        toast.success("Usuário criado");
      }
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const doDelete = async () => {
    try { await api.delete(`/users/${deleteId}`); toast.success("Usuário removido"); setDeleteId(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="users-page">
      <PageHeader icon={ShieldCheck} title="Gestão de Usuários" subtitle="Cadastre usuários e defina o nível de permissão de cada um">
        <Button onClick={openNew} className="bg-[#0B2545] hover:bg-[#134074]" data-testid="users-new-btn"><Plus className="mr-1.5 h-4 w-4" /> Novo usuário</Button>
      </PageHeader>

      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {ROLES.map((r) => (
          <div key={r} className="rounded-xl border border-[#E2E8F0] bg-white p-4 shadow-sm">
            <Badge2 color={ROLE_COLORS[r]}>{ROLE_LABELS[r]}</Badge2>
            <p className="mt-2 text-xs text-slate-500">{ROLE_DESC[r]}</p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-xl border border-[#E2E8F0] bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E2E8F0] bg-slate-50 text-left">
                {["Nome", "E-mail", "Perfil", "Setor", "Status"].map((h) => <th key={h} className="whitespace-nowrap px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">{h}</th>)}
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Ações</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-12 text-center"><Search className="mx-auto mb-2 h-6 w-6 text-slate-300" /><p className="text-slate-400">Nenhum usuário</p></td></tr>
              ) : users.map((u) => (
                <tr key={u.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60" data-testid="users-row">
                  <td className="px-4 py-3 font-medium text-slate-800">{u.name}</td>
                  <td className="px-4 py-3">{u.email}</td>
                  <td className="px-4 py-3"><Badge2 color={ROLE_COLORS[u.role]}>{ROLE_LABELS[u.role]}</Badge2></td>
                  <td className="px-4 py-3">{u.department}</td>
                  <td className="px-4 py-3"><Badge2 color={u.active ? "green" : "red"}>{u.active ? "Ativo" : "Inativo"}</Badge2></td>
                  <td className="whitespace-nowrap px-4 py-3 text-right">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(u)} data-testid={`users-edit-${u.id}`}><Pencil className="h-4 w-4 text-slate-500" /></Button>
                    {u.id !== me.id && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setDeleteId(u.id)} data-testid={`users-delete-${u.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[520px]" data-testid="users-dialog">
          <DialogHeader><DialogTitle className="text-[#0B2545]">{editing ? "Editar" : "Novo"} usuário</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-4 py-2 sm:grid-cols-2">
            <div className="sm:col-span-2"><Label className="mb-1.5 block text-xs font-semibold text-slate-600">Nome *</Label><Input value={form.name || ""} onChange={(e) => setF("name", e.target.value)} data-testid="users-field-name" /></div>
            <div className="sm:col-span-2"><Label className="mb-1.5 block text-xs font-semibold text-slate-600">E-mail *</Label><Input type="email" value={form.email || ""} disabled={!!editing} onChange={(e) => setF("email", e.target.value)} data-testid="users-field-email" /></div>
            <div className="sm:col-span-2"><Label className="mb-1.5 block text-xs font-semibold text-slate-600">{editing ? "Nova senha (opcional)" : "Senha *"}</Label><Input type="password" value={form.password || ""} onChange={(e) => setF("password", e.target.value)} data-testid="users-field-password" /></div>
            <div>
              <Label className="mb-1.5 block text-xs font-semibold text-slate-600">Perfil / Permissão</Label>
              <Select value={form.role} onValueChange={(v) => setF("role", v)}><SelectTrigger data-testid="users-field-role"><SelectValue /></SelectTrigger><SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent></Select>
            </div>
            <div>
              <Label className="mb-1.5 block text-xs font-semibold text-slate-600">Setor</Label>
              <Select value={form.department} onValueChange={(v) => setF("department", v)}><SelectTrigger data-testid="users-field-dept"><SelectValue /></SelectTrigger><SelectContent>{DEPTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select>
            </div>
            {editing && (
              <div className="sm:col-span-2">
                <Label className="mb-1.5 block text-xs font-semibold text-slate-600">Status</Label>
                <Select value={form.active ? "Ativo" : "Inativo"} onValueChange={(v) => setF("active", v === "Ativo")}><SelectTrigger data-testid="users-field-status"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Ativo">Ativo</SelectItem><SelectItem value="Inativo">Inativo</SelectItem></SelectContent></Select>
              </div>
            )}
            {form.role && <p className="sm:col-span-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">{ROLE_DESC[form.role]}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-[#0B2545] hover:bg-[#134074]" data-testid="users-save-btn">{saving ? "Salvando..." : "Salvar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Remover usuário</AlertDialogTitle><AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={doDelete} className="bg-rose-600 hover:bg-rose-700">Excluir</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
